/**
 * User Preference Manager
 *
 * Handles role-based user preferences and configurations.
 * Supports migration from global preferences to role-based preferences.
 */
import { CURRENT_VERSIONS } from './sectionVersions';

/**
 * Get the current user role from localStorage
 * @returns {string} - The current user role
 */
const getCurrentRole = () => {
  return localStorage.getItem("role") || "defaultRole";
};

/**
 * Generate role-based key
 * @param {string} baseKey - The base key without role prefix
 * @returns {string} - Role-based key (e.g., "PM_selectedColumns_inventory")
 */
export const getRoleBasedKey = (baseKey) => {
  const role = getCurrentRole();
  return `${role}_${baseKey}`;
};

/**
 * Check if role-based preference exists
 * @param {string} baseKey - The base key without role prefix
 * @returns {boolean} - True if role-based preference exists
 */
export const hasRoleBasedPreference = (baseKey) => {
  const roleKey = getRoleBasedKey(baseKey);
  return localStorage.getItem(roleKey) !== null;
};

/**
 * Get preference value with role-based fallback
 * @param {string} baseKey - The base key without role prefix
 * @param {any} defaultValue - Default value if no preference found
 * @returns {any} - The preference value
 */
export const getPreference = (baseKey, defaultValue = null) => {
  const roleKey = getRoleBasedKey(baseKey);
  
  // First try to get role-based preference
  const roleBasedValue = localStorage.getItem(roleKey);
  if (roleBasedValue !== null) {
    try {
      return JSON.parse(roleBasedValue);
    } catch (e) {
      return roleBasedValue;
    }
  }
  
  // If no role-based preference, check for global preference
  const globalValue = localStorage.getItem(baseKey);
  if (globalValue !== null) {
    try {
      const parsedValue = JSON.parse(globalValue);
      // Migrate global preference to role-based
      setPreference(baseKey, parsedValue);
      return parsedValue;
    } catch (e) {
      // Handle string values
      setPreference(baseKey, globalValue);
      return globalValue;
    }
  }
  
  // Return default value if nothing found
  return defaultValue;
};

/**
 * Set role-based preference
 * @param {string} baseKey - The base key without role prefix
 * @param {any} value - The value to store
 */
export const setPreference = (baseKey, value) => {
  const roleKey = getRoleBasedKey(baseKey);
  
  if (typeof value === 'object') {
    localStorage.setItem(roleKey, JSON.stringify(value));
  } else {
    localStorage.setItem(roleKey, value);
  }
};

/**
 * Remove role-based preference
 * @param {string} baseKey - The base key without role prefix
 */
export const removePreference = (baseKey) => {
  const roleKey = getRoleBasedKey(baseKey);
  localStorage.removeItem(roleKey);
};

/**
 * Migrate global preferences to role-based for a specific section
 * @param {string} sectionKey - The section identifier (e.g., 'inventory', 'order_0-1')
 * @returns {Array} - Array of keys that were migrated
 */
export const migrateToRoleBased = (sectionKey) => {
  const keysToMigrate = [
    `selectedColumns_${sectionKey}`,
    `columnOrder_${sectionKey}`,
    `fullColumnOrder_${sectionKey}`,
    `columnWidths_${sectionKey}`,
    `columnConfigVersion_${sectionKey}`
  ];
  
  const migratedKeys = [];
  
  keysToMigrate.forEach(key => {
    const globalValue = localStorage.getItem(key);
    if (globalValue !== null && !hasRoleBasedPreference(key)) {
      // Migrate to role-based
      setPreference(key, globalValue);
      migratedKeys.push(key);
      console.log(`Migrated ${key} to role-based preference`);
    }
  });
  
  return migratedKeys;
};

/**
 * Get universal column config version for migration purposes
 * @returns {number} - The universal version number
 */
export const getUniversalColumnConfigVersion = () => {
  const universalVersion = localStorage.getItem('universalColumnConfigVersion');
  return universalVersion ? parseInt(universalVersion, 10) : 0;
};

/**
 * Set universal column config version
 * @param {number} version - The version number to set
 */
export const setUniversalColumnConfigVersion = (version) => {
  localStorage.setItem('universalColumnConfigVersion', version.toString());
};

/**
 * Check if migration to role-based preferences is needed
 * @returns {boolean} - True if migration is needed
 */
export const needsMigration = () => {
  const universalVersion = getUniversalColumnConfigVersion();
  // Version 0 means no migration has been done yet
  return universalVersion === 0;
};

/**
 * Clean up old global keys after successful migration
 * @param {Array} keysToCleanup - Array of keys to remove from localStorage
 */
export const cleanupOldKeys = (keysToCleanup) => {
  if (!keysToCleanup || keysToCleanup.length === 0) return;
  
  let cleanedCount = 0;
  keysToCleanup.forEach(key => {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      cleanedCount++;
      console.log(`Cleaned up old global key: ${key}`);
    }
  });
  
  if (cleanedCount > 0) {
    console.log(`Cleaned up ${cleanedCount} old global preference keys`);
  }
};

/**
 * Perform complete migration of all preferences to role-based
 */
export const performCompleteMigration = () => {
  const currentRole = getCurrentRole();
  console.log(`Starting migration to role-based preferences for role: ${currentRole}`);
  
  // List of all section keys that might exist
  const possibleSections = Object.keys(CURRENT_VERSIONS);

  // Collect all migrated keys for cleanup
  const allMigratedKeys = [];
  
  // Migrate each section
  possibleSections.forEach(section => {
    const migratedKeys = migrateToRoleBased(section);
    allMigratedKeys.push(...migratedKeys);
  });
  
  // Mark migration as complete
  setUniversalColumnConfigVersion(1);
  
  // Clean up old keys after successful migration
  if (allMigratedKeys.length > 0) {
    cleanupOldKeys(allMigratedKeys);
    console.log(`Migration completed. Migrated ${allMigratedKeys.length} preferences and cleaned up old keys.`);
  } else {
    console.log('Migration completed. No preferences needed migration.');
  }
};

/**
 * Manually trigger cleanup of all possible old global keys
 * This can be called if you want to clean up any remaining old keys
 */
export const cleanupAllOldGlobalKeys = () => {
  const possibleSections = Object.keys(CURRENT_VERSIONS);

  const keyTypes = [
    'selectedColumns',
    'columnOrder',
    'fullColumnOrder',
    'columnWidths',
    'columnConfigVersion'
  ];
  
  const keysToCleanup = [];
  
  // Generate all possible old global keys
  possibleSections.forEach(section => {
    keyTypes.forEach(keyType => {
      keysToCleanup.push(`${keyType}_${section}`);
    });
  });
  
  cleanupOldKeys(keysToCleanup);
};

/**
 * Initialize role-based preferences on app start
 */
export const initializeRoleBasedPreferences = () => {
  if (needsMigration()) {
    performCompleteMigration();
  }
};
